import { Component, inject, OnDestroy } from '@angular/core';
import { NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { HorizontalBarChartComponent, HorizontalBarChartData } from '../../../../../_shared/components/graphs/horizontal-bar-chart/horizontal-bar-chart.component';
import { GraphErrorStateComponent } from '../../../../../_shared/components/graphs/graph-error-state/graph-error-state.component';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { OvertimeInsights } from '../../../models/overtime-insights.model';
import { Subject, Subscription } from 'rxjs';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { DriverOperationsService } from '../../../services/driver-operations.service';
import { endOfDay, startOfMonth } from 'date-fns';
import { PaginationRequest } from '../../../../../_shared/models/pagination-request.model';
import { FilterService } from '../../../../../_shared/services';
import { TranslocoPipe } from '@jsverse/transloco';

@Component({
  selector: 'app-highest-overtime-graph',
  standalone: true,
  imports: [
    TranslocoPipe,
    NgbTooltipModule,
    HorizontalBarChartComponent,
    GraphErrorStateComponent,
    EmptyStateComponent
],
  templateUrl: './highest-overtime-graph.component.html',
  styleUrl: './highest-overtime-graph.component.scss'
})
export class HighestOvertimeGraphComponent implements OnDestroy {
  private driverOperationsService = inject(DriverOperationsService);
  private errorHandlerService = inject(ErrorHandlerService);
  private filterService = inject(FilterService);

  private filterSubscription: Subscription;

  overtimeData: OvertimeInsights[] = [];
  isLoading: boolean = true;
  hasError: boolean = false;
  paginationRequest: PaginationRequest = {
    page: 1,
    pageSize: 5,
    timeFrom: startOfMonth(new Date()),
    timeTo: endOfDay(new Date()),
    accountIds: []
  };

  private readonly barColor = '#FF7F00';
  private cancelSubject: Subject<void> = new Subject<void>();

  chartData: HorizontalBarChartData[] = [];

  constructor() {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe((filter) => {
      // Guard: Skip if filter is empty/null
      if (Object.values(filter).every(x => x === null) || filter === null) {
        return;
      }
      this.paginationRequest.accountIds = filter.accountIds;
      this.getHighestOvertime();
    });
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async getHighestOvertime(): Promise<void> {
    this.hasError = false;
    this.isLoading = true;

    try {
      this.cancelSubject.next();
      const response = await this.driverOperationsService.getHightestOvertime(this.paginationRequest, this.cancelSubject);

      if (!response?.data?.results?.length) {
        this.overtimeData = [];
        this.isLoading = false;
        return;
      }

      this.overtimeData = response.data.results;
      this.chartData = this.overtimeData.map(item => ({
        name: `${item.firstName} ${item.lastName}`,
        value: Math.round(item.totalOvertime ?? 0),
        color: this.barColor
      }));

      this.isLoading = false;
    } catch (error: any) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  get hasData(): boolean {
    return this.chartData.length > 0;
  }

  get maxOvertimeValue(): number {
    if (!this.chartData.length) return 100;
    const maxValue = Math.max(...this.chartData.map(d => d.value));
    // Round up to nearest 5
    return Math.ceil(maxValue / 5) * 5 || 5;
  }

  get xTickInterval(): number {
    return Math.ceil(this.maxOvertimeValue / 5) || 1;
  }
}